import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/auth-store.js';
import { ApiClientError } from '../lib/api.js';
import { dayjs } from '../lib/format.js';

interface LocationState {
  from?: string;
}

const DHAKA_TZ = 'Asia/Dhaka';

/**
 * Account-locked errors carry a `details.retryAfter` ISO string. Format
 * it into a human sentence in the operator's local Dhaka time so the
 * user reads "Try again at 10:38 PM Dhaka (in 14 minutes)." rather than
 * the raw UTC timestamp the backend ships.
 */
function formatLockoutMessage(err: ApiClientError): string | null {
  if (err.code !== 'ACCOUNT_LOCKED') return null;
  const retry = err.details?.retryAfter;
  if (typeof retry !== 'string') return err.message;
  const at = dayjs(retry).tz(DHAKA_TZ);
  if (!at.isValid()) return err.message;
  const when = at.format('h:mm A');
  const inWhen = at.fromNow(true);
  return `${err.message} Try again at ${when} Dhaka (in ${inWhen}).`;
}

export function LoginPage() {
  const status = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);
  const login = useAuthStore((s) => s.login);
  const storeError = useAuthStore((s) => s.error);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [displayError, setDisplayError] = useState<string | null>(null);

  const location = useLocation();
  const state = location.state as LocationState | null;

  if (user) {
    // Role-aware redirect. Officers and operators always go to their role
    // home — honouring a stored `from` could drop them on a CO-only page
    // (e.g. /approvals left over from a prior CO session) and show an
    // Access-denied card. Only the CO restores a deep-linked `from`.
    const from = state?.from && state.from !== '/login' ? state.from : null;
    const redirectTo =
      user.role === 'OFFICER' ? '/my-work' : user.role === 'DATA_ENTRY' ? '/entry' : (from ?? '/');
    return <Navigate to={redirectTo} replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setFieldErrors({});
    setDisplayError(null);
    try {
      await login(username, password);
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.errors) setFieldErrors(err.errors);
        const lockout = formatLockoutMessage(err);
        if (lockout) setDisplayError(lockout);
      }
    }
  }

  // Prefer the locally-formatted message (lockout with retry time) over
  // the bare store message when both are present.
  const error = displayError ?? storeError;

  function onSubmit(event: FormEvent<HTMLFormElement>): void {
    void handleSubmit(event);
  }

  const submitting = status === 'authenticating';

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-bn-800 to-bn-950 p-4">
      {/*
       * Subtle topographic motif. Inline SVG — concentric ellipses tinted
       * with low-opacity white strokes, drawn off-center so the card sits
       * over the eye of the pattern. Decorative only, aria-hidden, ~1.5kb
       * gzipped.
       */}
      <svg
        aria-hidden="true"
        viewBox="0 0 1200 800"
        preserveAspectRatio="xMidYMid slice"
        className="pointer-events-none absolute inset-0 h-full w-full"
      >
        <g
          fill="none"
          stroke="rgba(255,255,255,0.05)"
          strokeWidth="1.4"
          transform="translate(420 360)"
        >
          <ellipse rx="80" ry="46" />
          <ellipse rx="160" ry="92" />
          <ellipse rx="240" ry="138" />
          <ellipse rx="320" ry="184" />
          <ellipse rx="400" ry="230" />
          <ellipse rx="480" ry="276" />
          <ellipse rx="560" ry="322" />
          <ellipse rx="640" ry="368" />
        </g>
        {/* Soft accent highlight in the upper-left. */}
        <circle cx="220" cy="120" r="180" fill="url(#login-glow)" />
        <defs>
          <radialGradient id="login-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.08)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0)" />
          </radialGradient>
        </defs>
      </svg>

      <div className="relative w-full max-w-md card">
        <div className="text-center">
          <img
            src="/11sigbn-logo.png"
            alt="11 Signal Battalion"
            className="mx-auto h-24 w-24 object-contain"
          />
          <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-bn-700">
            11 Signal Battalion
          </p>
          <h1 className="mt-1 text-2xl font-semibold text-slate-900">Odysseus</h1>
          <p className="mt-1 text-sm text-slate-500">Sign in to continue</p>
        </div>

        <form className="mt-6 space-y-4" onSubmit={onSubmit} noValidate>
          <div>
            <label htmlFor="username" className="form-label">
              Username or BA Number
            </label>
            <input
              id="username"
              type="text"
              autoComplete="username"
              autoFocus
              required
              className="form-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={submitting}
              aria-invalid={fieldErrors.username ? 'true' : 'false'}
            />
            {fieldErrors.username?.map((m) => (
              <p key={m} className="form-error">
                {m}
              </p>
            ))}
          </div>

          <div>
            <label htmlFor="password" className="form-label">
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              className="form-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
              aria-invalid={fieldErrors.password ? 'true' : 'false'}
            />
            {fieldErrors.password?.map((m) => (
              <p key={m} className="form-error">
                {m}
              </p>
            ))}
          </div>

          {error && !Object.keys(fieldErrors).length && (
            <div role="alert" className="rounded-md bg-rag-red-soft px-3 py-2 text-sm text-rag-red">
              {error}
            </div>
          )}

          <button type="submit" className="btn-primary w-full" disabled={submitting}>
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p className="mt-4 text-center text-sm text-slate-500">
          Officer without an account?{' '}
          <Link to="/register" className="font-medium text-bn-700 hover:text-bn-600">
            Register here
          </Link>
        </p>
      </div>
    </div>
  );
}
